import { yupResolver } from '@hookform/resolvers/yup';
import { CountryCode } from 'libphonenumber-js';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'utils/general/storage';

import { useForgotPassword } from '@/modules/auth/apiHooks';
import { TAB_QUERY_INDICATOR } from '@/modules/auth/components/FormsContainer/Login';
import { useResetPasswordHook } from '@/modules/auth/components/FormsContainer/Password/logic';
import Header from '@/modules/auth/components/Header';
import { IAuth } from '@/modules/auth/types';
import {
  Button,
  InputField,
  PhoneInputWithCountry,
} from '@/shared/components/atoms';
import Tabs from '@/shared/components/atoms/Tabs';
import routes from '@/shared/constant/routes';

import Arrow from '~/images/shared/arrow-down.svg';

const ForgotPassword = () => {
  const { query, push } = useRouter();
  const { t } = useTranslation('login');
  const loginType = query[TAB_QUERY_INDICATOR] as 'phoneNumber' | 'email';
  const { ForgotPasswordFormValidationSchema } = useResetPasswordHook();
  const { getCookie } = useCookies();
  const countryCode = useMemo(
    () => getCookie('defaultCountryCode') ?? 'EG',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      phoneNumber: '',
      country: countryCode,
      email: '',
    } as IAuth.IForgotPasswordSchema,
    resolver: yupResolver(ForgotPasswordFormValidationSchema),
  });

  const {
    register,
    control,
    getValues,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = methods;

  const { mutate: forgotPassword, isLoading: isForgotPasswordLoading } =
    useForgotPassword();

  const onSubmit = (data: IAuth.IForgotPasswordSchema) => {
    if (loginType === 'phoneNumber') {
      forgotPassword({
        phoneNumber: data.phoneNumber,
        country: data.country,
      } as IAuth.ILoginPhoneNumberSchema);
    } else if (loginType === 'email') {
      forgotPassword({
        email: data.email,
      } as IAuth.ILoginEmailSchema);
    }
  };

  useEffect(() => {
    setValue('country', countryCode);
    setValue('phoneNumber', '');
    setValue('email', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginType]);

  useEffect(() => {
    if (loginType === 'phoneNumber') {
      clearErrors('email');
    } else if (loginType === 'email') {
      clearErrors('phoneNumber');
      clearErrors('country');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginType, errors]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-16'>
        <Header
          title={t('forgot-password')}
          subtitle={t('forgot-password-subtitle')}
        />
        <Tabs
          tabsList={[
            { label: t('mobile-number'), key: 'phoneNumber' },
            { label: t('email-address'), key: 'email' },
          ]}
          getLabel={(tab) => tab.label}
          getValue={(tab) => tab.key}
          variant='underline'
          tabQueryIndicator={TAB_QUERY_INDICATOR}
          containerClassName='w-full'
          tabClasseName='text-14 font-medium'
          defaultValue='phoneNumber'
        />
        {loginType === 'phoneNumber' && (
          <div className='mt-16 flex flex-col gap-0'>
            <PhoneInputWithCountry
              label={t('mobile-number')}
              width='w-full'
              id='phoneNumber'
              defaultCountry={getValues('country') as CountryCode}
              name='phoneNumber'
              control={control}
              placeholder={t('enter-mobile-number')}
              required
              errorMessage={errors?.phoneNumber?.message}
              className='rounded-xl bg-background-transparent py-2'
              borderColor='border-outline-variant border-xs'
              onCountryChange={(country) => setValue('country', country)}
              autoComplete='off'
            />
          </div>
        )}
        {loginType === 'email' && (
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-14'
            label={t('email-address')}
            placeholder={t('email-address')}
            error={!!errors.email}
            errorMessage={errors.email?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('email')}
            autoComplete='off'
          />
        )}
        <div className='flex flex-col gap-32'>
          <Button
            size='full'
            type='submit'
            className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
            disabled={
              isForgotPasswordLoading ||
              (loginType === 'phoneNumber' &&
                (getValues('phoneNumber') === '' || !!errors.phoneNumber)) ||
              (loginType === 'email' &&
                (getValues('email') === '' || !!errors.email))
            }
          >
            {t('send-reset-link')}
          </Button>
          <div
            className='flex cursor-pointer justify-center gap-2 text-14 font-semi-bold text-primary-default'
            onClick={() => {
              push(routes.login);
            }}
          >
            <span className='flex rotate-90 items-center justify-center'>
              <Arrow />
            </span>
            <span>{t('return-login')}</span>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default ForgotPassword;
